<template>
  <div class="point-info-page">
    <div class="navigation-header mobile-only">
      <button @click="$router.back()" class="back-button">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <h1 class="header-title">About Points</h1>
    </div>

    <div class="desktop-header desktop-only">
        <div class="container">
            <button @click="$router.back()" class="desktop-back">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                Back
            </button>
            <h1 class="desktop-title">About Points</h1>
        </div>
    </div>

    <div class="content-container">
      <!-- Section 1: Earning Points -->
      <section class="info-section">
        <div class="illustration-wrapper">
          <img src="/img/member-img/poin-get.svg" alt="Earning Points" class="illustration">
        </div>
        <div class="text-content">
          <h2 class="section-title">Earning Points</h2>
          <ol class="info-list">
            <li>Earn points for every transaction through the application.</li>
            <li>Points are calculated based on your transaction value. Each transaction contributes to your point balance immediately after materialization.</li>
            <li>Points validity: Your points are valid for a specific duration (e.g., {{ expirationDisplay }}) from the date they were earned.</li>
          </ol>
        </div>
      </section>

      <!-- Section 2: Using Points -->
      <section class="info-section">
        <div class="illustration-wrapper">
          <img src="/img/member-img/poin-spend.svg" alt="Using Points" class="illustration">
        </div>
        <div class="text-content">
          <h2 class="section-title">Using & Redeeming Points</h2>
          <ol class="info-list">
            <li>Open the Rewards or Deals menu to see available vouchers and items you can redeem.</li>
            <li>Exchange your points for vouchers. Once redeemed, the voucher will be available in your "Vouchers" page.</li>
            <li>Vouchers earned from points redemption usually have their own expiration date, different from point expiration.</li>
            <li>Points are consumed using a <strong>FIFO (First-In-First-Out)</strong> method. This means your oldest points are always used first when you redeem a voucher.</li>
          </ol>
        </div>
      </section>

      <!-- Section 3: Expiration Example -->
      <section class="info-section">
        <div class="text-content">
          <h2 class="section-title">Point Expiration</h2>
          <p class="section-description">
            To ensure you get the most out of your membership, points have an expiration period. We use a FIFO system to track points fairly.
          </p>
          <div class="example-box">
            <h3 class="example-title">Example Scenario:</h3>
            <p class="example-text">
              If you earn 10 Points in August 2023 and 20 Points in September 2023:
            </p>
            <ul class="example-list">
              <li>The 10 points from August will expire first (e.g., August {{ nextYear }}).</li>
              <li>The 20 points from September will expire later (e.g., September {{ nextYear }}).</li>
              <li>If you spend 5 points in October 2023, those 5 points are deducted from the August batch.</li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue'
import { usePointSystem } from '@/composables/pointsystem'

export default {
  name: 'PointInfo',
  setup() {
    const { metadata } = usePointSystem()

    const expirationDays = computed(() => metadata.value?.point_expiration?.expiration_days || 365)
    
    const expirationDisplay = computed(() => {
        const days = expirationDays.value
        if (days === 365) return '12 months'
        if (days % 30 === 0) return `${days / 30} months`
        return `${days} days`
    })

    const nextYear = computed(() => {
        // Logic for example year
        if (expirationDays.value >= 365) return '2024'
        return '2023 (or soon after)'
    })

    return {
        expirationDisplay,
        nextYear
    };
  }
}
</script>

<style scoped>
.point-info-page {
  min-height: 100vh;
  background-color: #f9fafb;
  padding-bottom: 40px;
}

.navigation-header {
  display: flex;
  align-items: center;
  padding: 16px;
  background-color: white;
  position: sticky;
  top: 0;
  z-index: 10;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.back-button {
  background: none;
  border: none;
  padding: 8px;
  margin-right: 8px;
  cursor: pointer;
  color: #374151;
}

.header-title {
  font-size: 18px;
  font-weight: 600;
  padding: 0;
  margin: 0;
  color: #111827;
}

.desktop-header {
    background-color: white;
    padding: 24px 0;
    margin-bottom: 32px;
    border-bottom: 1px solid #e5e7eb;
}

.desktop-header .container {
    max-width: 800px;
    margin: 0 auto;
    padding: 0 24px;
    display: flex;
    align-items: center;
    gap: 16px;
}

.desktop-back {
    display: flex;
    align-items: center;
    gap: 4px;
    background: none;
    border: none;
    color: #6b7280;
    cursor: pointer;
    font-size: 14px;
    transition: color 0.2s;
}

.desktop-back:hover {
    color: #111827;
}

.desktop-title {
    font-size: 24px;
    font-weight: 700;
    color: #111827;
    margin: 0;
}

.content-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 48px;
}

.info-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
}

.illustration-wrapper {
  width: 100%;
  max-width: 400px;
  display: flex;
  justify-content: center;
}

.illustration {
  width: 100%;
  height: auto;
  object-fit: contain;
}

.text-content {
  width: 100%;
}

.section-title {
  font-size: 20px;
  font-weight: 700;
  color: var(--primary-color, #b91c1c);
  margin-bottom: 16px;
  text-align: center;
}

.info-list {
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-list li {
  font-size: 15px;
  line-height: 1.6;
  color: #4b5563;
}

.section-description {
  font-size: 15px;
  line-height: 1.6;
  color: #4b5563;
  margin-bottom: 20px;
}

.example-box {
  background-color: #fef9f2;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #fef4e2;
}

.example-title {
  font-size: 16px;
  font-weight: 600;
  color: #b06900;
  margin-bottom: 12px;
}

.example-text {
  font-size: 14px;
  color: #b06900;
  margin-bottom: 8px;
}

.example-list {
  padding-left: 20px;
}

.example-list li {
  font-size: 14px;
  color: #b06900;
  margin-bottom: 4px;
}

.mobile-only {
    display: none;
}

@media (max-width: 900px) {
    .mobile-only {
        display: flex;
    }
    .desktop-only {
        display: none;
    }
    .content-container {
        padding: 16px;
        gap: 32px;
    }
    .info-section {
        gap: 16px;
    }
    .section-title {
        font-size: 18px;
        text-align: left;
    }
}
</style>
